import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory28() {
  const { lesson, course, accent } = findLesson("music-theory", "28");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT28-Hey-Jude-Animated.mp4"
        caption="Chord trees in Hey Jude — why F · C · B♭ · F works"
        hint="≈ 22 sec · watch first"
      />
      <Chapter number="28" eyebrow="Music Theory · Chapter Twenty-Eight" title="Chord trees · Part 3: Hey Jude">
        <p>
          Theory is most convincing when it predicts something we already love. So we'll
          take a song by arguably the greatest pop songwriters of all time — the Beatles —
          and use chord trees to derive the chord progression from the melody alone, with
          our hands away from the audio. If the tree is good for anything, it should be
          good for this.
        </p>
        <p>
          The song is <strong><em>Hey Jude</em></strong>. The key is <strong>F major</strong>,
          the same key we mapped chapter by chapter in Part 2. That is convenient: the
          seven diatonic trees of F are already in our heads.
        </p>
        <MTQuote>
          A great song is one where you just don't know. You just don't know what they did
          first. And I don't think you should know, because that is the art of making a
          song, and that is how we should choose to leave it.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/28a-hey-jude-melodic-analysis.png"
          caption="Jason's analysis of 'Hey Jude' — the melody plotted with chord symbols above each landing point (Jude/bad/sad/song/better/heart/start/better). The Beatles' chord choices are entirely diatonic to F major; the melody walks through those chord tones with passing notes between them."
        />
      </Chapter>

      <Chapter eyebrow="Step one" title="Find the landing notes of the melody">
        <Prose>
          <p>
            Before any chord can be chosen, we have to find the <strong>landing notes</strong> —
            the melodic notes that carry weight, sit on strong beats, hold longer, or land
            on the most important word of the line. In <em>Hey Jude</em>, line one is:
          </p>
          <p className="font-mt italic text-lg text-amber-200">
            "Hey Jude, don't make it bad. Take a sad song, and make it better."
          </p>
          <p>
            Read it once aloud. The cadence of normal speech tells you the same answer
            you'd get from analysing pitches: <em>Jude</em>, <em>bad</em>, <em>sad</em>,
            <em> song</em>, <em>better</em>. Proper noun. Adjective. Adjective. Noun.
            Adjective. Important words, every one — and each lands on a strong beat with
            a longer note value than the syllables around it.
          </p>
        </Prose>

        <Steps>
          <Step><strong>Hey <em>Jude</em></strong> — landing note: <span className="font-mono text-amber-300">A</span></Step>
          <Step><strong>don't make it <em>bad</em></strong> — landing note: <span className="font-mono text-amber-300">G</span></Step>
          <Step><strong>take a <em>sad</em></strong> — landing notes: <span className="font-mono text-amber-300">B♭</span> and <span className="font-mono text-amber-300">F</span> (both held, equal weight)</Step>
          <Step><strong>and make it <em>better</em></strong> — landing note: <span className="font-mono text-amber-300">A</span> (last syllable lands; before that, <span className="font-mono">D</span> on "bett-")</Step>
        </Steps>
      </Chapter>

      <Chapter eyebrow="Step two" title="Pick branches from each tree">
        <Prose>
          <p>
            For each landing note, ask the diatonic tree of F major what chords contain
            that note. Three options each. Then narrow by context — the previous chord,
            the next chord, the colour the lyric wants.
          </p>
          <p>
            <strong>Jude (A):</strong> the diatonic tree of A in F major holds
            <span className="font-mono"> F major</span> (A as 3rd),
            <span className="font-mono"> D minor</span> (A as 5th), and
            <span className="font-mono"> A minor</span> (A as root). Since the song
            opens here, the Beatles will almost certainly establish the key — which means
            <strong> the tonic</strong>. Pick <span className="font-mono text-amber-300">F major</span>.
          </p>
          <p>
            <strong>Bad (G):</strong> the tree of G in F major gives
            <span className="font-mono"> G minor</span>,
            <span className="font-mono"> C major</span>, and
            <span className="font-mono"> E diminished</span>. All three sound musical
            here. The Beatles take <strong>C major</strong> — the V chord, building
            tension that will pull us back to the I. (E diminished would also work; the
            song doesn't use it.)
          </p>
          <p>
            <strong>Sad / song (B♭ and F):</strong> the trees of B♭ and F overlap. B♭ has
            <span className="font-mono"> B♭ major</span> (root),
            <span className="font-mono"> G minor</span> (3rd), and
            <span className="font-mono"> E diminished</span> (♭5th); F has
            <span className="font-mono"> F major</span> (root),
            <span className="font-mono"> B♭ major</span> (5th), and
            <span className="font-mono"> D minor</span> (3rd). Only one diatonic triad holds
            <strong> both</strong> — B♭ major. But listen to which note actually carries the
            line: the weight is on <strong>B♭</strong>, on "sad", and the F slips past it.
            So the Beatles reach past the triads for a chord built around that B♭:
            <strong> C7</strong>. Its notes are <span className="font-mono">C E G B♭</span> —
            a V chord whose added 7th <em>is</em> the landing note. That is why it glides
            into the next chord like a magnet: the B♭ is already leaning down towards A.
          </p>
          <p>
            <strong>Better (A, ending on the long A):</strong> back home. <strong>F major</strong>.
            Full stop in the sentence; full stop in the harmony.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The line, harmonised" title="Hey Jude, line one">
        <TheoryBox label="What we predicted, what they played">
          <div className="space-y-2 font-mt text-base text-amber-100">
            <div><strong>Hey Jude</strong> &nbsp;·&nbsp; <span className="font-mono">F major</span></div>
            <div><strong>don't make it bad</strong> &nbsp;·&nbsp; <span className="font-mono">C major</span></div>
            <div><strong>take a sad song</strong> &nbsp;·&nbsp; <span className="font-mono">C7</span></div>
            <div><strong>and make it better</strong> &nbsp;·&nbsp; <span className="font-mono">F major</span></div>
          </div>
          <div className="mt-3 text-[12px] text-nsm-dim">
            I &nbsp;→&nbsp; V &nbsp;→&nbsp; V7 &nbsp;→&nbsp; I — the home-and-away
            cadence at the heart of Western harmony, dressed in one of the best-known
            melodies of the 20th century. The chord tree gave us all four answers without ever touching the
            recording.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            What's striking is how few wrong options the tree even allowed. Three
            candidates per landing note, narrowed by the rule that the song should start
            on the tonic, that V wants to resolve, that B♭ in the melody wants C7
            underneath because they share the note. By the time we got to "better," the
            chord almost picked itself.
          </p>
          <p>
            The chord tree on the next line works the same way. Try it as homework: find
            the landing notes, list the three diatonic candidates for each one, and pick
            by ear or by harmonic logic.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Try every tree of F" title="The tool we used">
        <ChordTree tonic="F" mode="major" />

        <Prose>
          <p>
            Click any chord button to hear it. Then sing the melody fragment the chord is
            meant to support and listen for the lock-in. That is what reharmonisation
            sounds like when it works — the moment your ear says "yes, that one."
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A note about A minor" title="The chord that was wrong">
        <Prose>
          <p>
            One subtlety from the workout: A minor is part of F major's diatonic palette.
            And A minor contains both A and C. So why doesn't A minor sound right under
            the word <em>sad</em>?
          </p>
          <p>
            Because the melody on <em>sad</em> is B♭, not C. A minor (A&nbsp;C&nbsp;E)
            doesn't contain B♭. The tree of B♭ doesn't include A minor. The diatonic
            tree, used with discipline, automatically rules out chords that don't
            actually contain the landing note — which protects you from picking a
            superficially "in-key" chord that clashes with the held melody pitch.
          </p>
          <p>
            That is what makes the diatonic chord tree more than a mnemonic. It is a
            constraint system, not a checklist. You ask the right question — does this
            chord contain the landing note? — and the wrong answers fall away.
          </p>
        </Prose>

        <TheoryBox label="The lesson of Hey Jude">
          You don't need to listen to a song to figure out its chords. With a clear sense
          of the key, the landing notes of the melody, and a chord tree per note, you can
          derive a defensible progression before the audio is even playing. Theory is
          preparation; the ear is instinct. Together they figure out anything.
        </TheoryBox>
        <NoteImage
          src="/notes/music-theory/finals/28b-strong-beat-rules.png"
          caption="Jason's rules for the 'strong beat' — usually beat 1 or any down-beat, played longer than its neighbours, lands on a chord tone (root / 3rd / 5th / 7th / jazz tension), is the resolution point of any melodic climb, and aligns with adjectives, proper nouns, or other lyric stresses."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-29"
        label="Chapter 29 · Chord Trees - Pirates (Part 4)"
        why="Now we apply the chord tree to an instrumental piece — no lyrics, no proper nouns. He's a Pirate is in D minor, and the predictive game gets even sharper."
      />
    </LessonShell>
  );
}
